export type OctagonalWheelSpec = {
  x: number
  y: number
  z: number
  radius: number
  travelM: number
  naturalFrequencyHz: number
  dampingRatio: number
  sprungMassKg: number
  axle: 'front' | 'rear'
}

export type OctagonalGroundContact = {
  height: number
}

export type OctagonalWheelState = {
  compressionM: number
  contact: boolean
  loadN: number
  supportRadiusM: number
}

export type OctagonalRideState = {
  heightM: number
  verticalVelocityMps: number
  pitchRadians: number
  pitchVelocityRadiansPerSecond: number
  rollRadians: number
  rollVelocityRadiansPerSecond: number
  groundedWheels: number
  wheels: OctagonalWheelState[]
}

export type OctagonalRideTuning = {
  massKg: number
  lengthM: number
  widthM: number
  heightM: number
  wheelbaseM: number
  frontWeightFraction: number
  antiRollFront: number
  antiRollRear: number
  centreOfMassHeightM?: number
  longitudinalAccelerationMps2?: number
  lateralAccelerationMps2?: number
  gravity?: number
}

export function octagonalSupportRadius(radius: number, rotationRadians: number) {
  let support = 0
  for (let vertex = 0; vertex < 8; vertex += 1) {
    const theta = vertex * Math.PI / 4 - rotationRadians
    support = Math.max(support, -Math.sin(theta) * radius)
  }
  return support
}

export function createOctagonalRideState(heightM: number, wheelCount = 4): OctagonalRideState {
  return {
    heightM,
    verticalVelocityMps: 0,
    pitchRadians: 0,
    pitchVelocityRadiansPerSecond: 0,
    rollRadians: 0,
    rollVelocityRadiansPerSecond: 0,
    groundedWheels: wheelCount,
    wheels: Array.from({ length: wheelCount }, () => ({
      compressionM: 0,
      contact: true,
      loadN: 0,
      supportRadiusM: 0,
    })),
  }
}

export function resetOctagonalRideState(state: OctagonalRideState, heightM: number) {
  Object.assign(state, createOctagonalRideState(heightM, state.wheels.length))
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.max(minimum, Math.min(maximum, value))
}

export function stepOctagonalRide(
  state: OctagonalRideState,
  tuning: OctagonalRideTuning,
  wheelSpecs: readonly OctagonalWheelSpec[],
  groundContacts: readonly OctagonalGroundContact[],
  wheelRotationRadians: number | readonly number[],
  deltaSeconds: number,
) {
  const gravity = tuning.gravity ?? 9.81
  const steps = Math.max(1, Math.ceil(deltaSeconds / (1 / 240)))
  const delta = deltaSeconds / steps
  const pitchInertia = tuning.massKg * (tuning.lengthM ** 2 + tuning.heightM ** 2) / 12
  const rollInertia = tuning.massKg * (tuning.widthM ** 2 + tuning.heightM ** 2) / 12
  const centreOfMassZ = tuning.wheelbaseM * (0.5 - tuning.frontWeightFraction)

  while (state.wheels.length < wheelSpecs.length) {
    state.wheels.push({ compressionM: 0, contact: false, loadN: 0, supportRadiusM: 0 })
  }

  for (let substep = 0; substep < steps; substep += 1) {
    const forces = new Array<number>(wheelSpecs.length).fill(0)
    const stiffnesses = new Array<number>(wheelSpecs.length).fill(0)
    let deepestPenetration = 0

    for (let index = 0; index < wheelSpecs.length; index += 1) {
      const spec = wheelSpecs[index]
      const ground = groundContacts[index]
      const wheel = state.wheels[index]
      const rotation = typeof wheelRotationRadians === 'number'
        ? wheelRotationRadians
        : wheelRotationRadians[index] ?? 0
      const rawSupportRadius = octagonalSupportRadius(spec.radius, rotation)
      // Preserve the actual octagonal rolling radius, but feed it through the
      // tyre/suspension compliance that a real contact patch would provide.
      // Updating this inside every 240 Hz substep also removes the old pulsed
      // damping impulse caused by a 120 Hz wheel-rotation sample.
      const supportBlend = 1 - Math.exp(-delta / 0.04)
      const supportRadius = wheel.supportRadiusM > 0
        ? wheel.supportRadiusM + (rawSupportRadius - wheel.supportRadiusM) * supportBlend
        : rawSupportRadius
      const bumpTravel = spec.travelM * 0.58
      const droopTravel = spec.travelM * 0.42
      const anchorHeight = state.heightM + spec.y - state.pitchRadians * spec.z + state.rollRadians * spec.x
      const requestedCompression = ground.height + supportRadius - anchorHeight
      const contact = requestedCompression >= -droopTravel
      const compression = contact ? clamp(requestedCompression, -droopTravel, bumpTravel) : -droopTravel
      const compressionVelocity = wheel.contact === contact ? (compression - wheel.compressionM) / delta : 0
      const angularFrequency = 2 * Math.PI * spec.naturalFrequencyHz
      const stiffness = spec.sprungMassKg * angularFrequency * angularFrequency
      const damping = 2 * spec.dampingRatio * Math.sqrt(stiffness * spec.sprungMassKg)
      const preload = spec.sprungMassKg * gravity
      const force = contact
        ? clamp(preload + stiffness * compression + damping * compressionVelocity, 0, preload * 4.5)
        : 0

      wheel.compressionM = compression
      wheel.contact = contact
      wheel.supportRadiusM = supportRadius
      wheel.loadN = force
      forces[index] = force
      stiffnesses[index] = stiffness
      if (contact) deepestPenetration = Math.max(deepestPenetration, requestedCompression - bumpTravel)
    }

    const applyAntiRoll = (left: number, right: number, strength: number) => {
      if (!state.wheels[left]?.contact || !state.wheels[right]?.contact) return
      const averageStiffness = (stiffnesses[left] + stiffnesses[right]) * 0.5
      const transfer = (state.wheels[left].compressionM - state.wheels[right].compressionM)
        * averageStiffness * clamp(strength, 0, 1) * 0.5
      forces[left] = Math.max(0, forces[left] + transfer)
      forces[right] = Math.max(0, forces[right] - transfer)
    }
    applyAntiRoll(0, 1, tuning.antiRollFront)
    applyAntiRoll(2, 3, tuning.antiRollRear)

    let verticalForce = 0
    // Horizontal tyre acceleration acts through the centre of mass above the
    // contact plane. Feeding those inertial moments into the four suspension
    // corners makes longitudinal and lateral load transfer real tyre inputs,
    // rather than an axle-level approximation in the horizontal solver.
    const centreOfMassHeight = tuning.centreOfMassHeightM ?? 0
    let pitchTorque = centreOfMassZ * tuning.massKg * gravity
      + tuning.massKg * (tuning.longitudinalAccelerationMps2 ?? 0) * centreOfMassHeight
    let rollTorque = tuning.massKg * (tuning.lateralAccelerationMps2 ?? 0) * centreOfMassHeight
    state.groundedWheels = 0
    for (let index = 0; index < wheelSpecs.length; index += 1) {
      const force = forces[index]
      state.wheels[index].loadN = force
      const forceBearingThreshold = Math.max(25, wheelSpecs[index].sprungMassKg * gravity * 0.02)
      if (state.wheels[index].contact && force >= forceBearingThreshold) state.groundedWheels += 1
      verticalForce += force
      pitchTorque -= wheelSpecs[index].z * force
      rollTorque += wheelSpecs[index].x * force
    }

    state.verticalVelocityMps += (verticalForce / tuning.massKg - gravity) * delta
    state.heightM += state.verticalVelocityMps * delta
    state.pitchVelocityRadiansPerSecond += pitchTorque / pitchInertia * delta
    state.rollVelocityRadiansPerSecond += rollTorque / rollInertia * delta
    state.pitchRadians += state.pitchVelocityRadiansPerSecond * delta
    state.rollRadians += state.rollVelocityRadiansPerSecond * delta

    if (deepestPenetration > 0) {
      state.heightM += deepestPenetration
      if (state.verticalVelocityMps < 0) state.verticalVelocityMps *= -0.12
    }

    const maximumBodyAngle = 55 * Math.PI / 180
    if (Math.abs(state.pitchRadians) > maximumBodyAngle) {
      state.pitchRadians = Math.sign(state.pitchRadians) * maximumBodyAngle
      state.pitchVelocityRadiansPerSecond *= -0.15
    }
    if (Math.abs(state.rollRadians) > maximumBodyAngle) {
      state.rollRadians = Math.sign(state.rollRadians) * maximumBodyAngle
      state.rollVelocityRadiansPerSecond *= -0.15
    }
  }

  return state
}
